import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import CommonTable, { ITableColumn } from '@/components/common/CommonTable';
import MainHeading from '@/components/common/MainHeading';
import ViewDocumentModal from '@/components/common/ViewDocumentModal';
import ViewIcon from '@/components/Icons/ViewIcon';
import CardLayout from '@/components/Layout/CardLayout';
import Layout from '@/components/Layout/Layout';
import useFilter from '@/hooks/useFilter';
import {
  IPaySlipPageState,
  ISalaryResponse,
  ISalaryTableData,
} from '@/interfaces/salary.model';
import { setDateFilter } from '@/redux/features/UserSlice';
import { RootState } from '@/redux/store';
import {
  getFilterUrl,
  handleFormatAmount,
  handlePaySlipCalculation,
  showToastNotification,
} from '@/utility/helper';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';

export interface IGenrateSalaryResponse {
  status: boolean;
  message: string;
  data: string;
}

const MySlips = () => {
  const dispatch = useDispatch();
  const [selectedDocument, setSelectedDocument] = useState({
    url: '',
  });
  const userDetails = useSelector((state: RootState) => state?.user?.user);
  const dateFilter = useSelector((state: RootState) => state?.user?.months);
  const [pageStates, setPageStates] = useState<IPaySlipPageState>({
    currentPage: 1,
    count: 0,
    totalPages: 0,
    tableLoader: false,
    sortByDate: dateFilter,
    paySlipList: [],
    isLoadingGeneratePayslip: false,
  });
  const filterDetails = useSelector(
    (state: RootState) => state?.user?.filtersStates,
  );
  const [modal, setModal] = useState(false);
  const toggleModal = () => {
    setModal(!modal);
  };

  const tableHeading: ITableColumn<ISalaryTableData>[] = [
    { value: 'employeeCode', label: 'Employee Code' },
    { value: 'name', label: 'Employee' },
    { value: 'basicSalary', label: 'Basic Pay' },
    { value: 'grossSalary', label: 'Gross Pay' },
    { value: 'deductionAmount', label: 'Deduction' },
    { value: 'netSalary', label: 'Net Pay' },
  ];
  useEffect(() => {
    getPaySlip();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pageStates?.sortByDate,
    dateFilter,
    pageStates?.currentPage,
    pageStates?.pageSize,
  ]);

  const getPaySlip = async () => {
    try {
      const filter = {
        pageNumber: pageStates?.currentPage,
        pageSize: pageStates?.pageSize,
        startDate: dateFilter[0],
        endDate: dateFilter[1],
        userId: userDetails?.id,
      };
      const API = `${API_URL}${API_ENDPOINT?.getMonthlyUserSalary}?${getFilterUrl(filter)}`;
      setPageStates((prev) => ({
        ...prev,
        tableLoader: true,
      }));
      const response: ISalaryResponse = await callApi(API, {
        method: 'GET',
      });
      setPageStates((prev) => ({
        ...prev,
        tableLoader: false,
      }));
      if (response?.status === true) {
        const newData = response?.data?.userSalary?.map((item) => ({
          ...item,
          id: item?.id,
          employeeCode: item?.employeeCode,
          empNameWithImage: item?.name + '##' + item?.profilePhoto,
          basicSalary: item?.['Basic Salary']
            ? handleFormatAmount(item?.['Basic Salary'])
            : '--',
          ...(() => {
            const { deductionAmount, netSalary } = handlePaySlipCalculation(
              item?.['Gross Salary'],
              item?.['deduction'],
            );
            return {
              grossSalary: handleFormatAmount(item?.['Gross Salary']) || '--',
              deductionAmount: handleFormatAmount(deductionAmount),
              netSalary: handleFormatAmount(netSalary),
            };
          })(),
        }));
        setPageStates((pre) => ({
          ...pre,
          paySlipList: newData,
          count: response?.data?.pageInfo?.total ?? 0,
          totalPages: response?.data?.pageInfo?.totalPages,
        }));
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setPageStates((prev) => ({
        ...prev,
        tableLoader: false,
      }));
    }
  };

  const handleViewPayslip = async (data: ISalaryTableData) => {
    if (!data?.['Basic Salary']) {
      const [year, month] = pageStates?.sortByDate?.[0]?.split('-') || [];
      const shortMonths = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      const date = `${shortMonths[Number(month) - 1]} - ${year}`;
      showToastNotification('danger', `${date} salary is not created yet.`);
      return;
    }

    const filter = {
      pageNumber: pageStates?.currentPage,
      pageSize: pageStates?.pageSize,
      startDate: dateFilter[0],
      endDate: dateFilter[1],
      userId: userDetails?.id,
    };
    try {
      setPageStates((pre) => ({
        ...pre,
        isLoadingGeneratePayslip: true,
      }));
      const API = `${API_URL}${API_ENDPOINT?.genereatePaySlip}userId=${data?.id}&startDate=${filter?.startDate}&endDate=${filter?.endDate}`;
      const response: IGenrateSalaryResponse = await callApi(API, {
        method: 'GET',
      });
      if (response?.status === true) {
        setSelectedDocument({ url: response?.data });
        setModal(true);
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setPageStates((pre) => ({
        ...pre,
        isLoadingGeneratePayslip: true,
      }));
    }
  };

  const actionData = [
    {
      label: 'View',
      className: 'btn btn-link',
      icon: <ViewIcon />,
      title: 'view payslip',
      clickHandler: handleViewPayslip,
    },
  ];
  const singleDateChangeHandler = (date: Date | null) => {
    if (!date || !moment(date).isValid()) return;

    const selectedStartDate = moment(date)
      .startOf('month')
      .format('YYYY-MM-DD');
    const selectedEndDate = moment(date).endOf('month').format('YYYY-MM-DD');
    dispatch(setDateFilter([selectedStartDate, selectedEndDate]));
    setPageStates((prev) => ({
      ...prev,
      currentPage: 1,
    }));
  };

  const { handleChangePageSize, pageChangeHandler } = useFilter(
    pageStates,
    setPageStates,
  );
  return (
    <Layout>
      <div className="content">
        <MainHeading
          title={'My Pay Slip'}
          showDefaultDate={true}
          singleDateChangeHandler={singleDateChangeHandler}
          pageState={{
            sortBySingleDate: dateFilter,
          }}
          pickerType="month"
        />
        <CardLayout>
          <CommonTable<ISalaryTableData>
            tableHeading={tableHeading}
            tableData={pageStates?.paySlipList}
            ActionData={actionData}
            currentPage={pageStates?.currentPage}
            pageChangeHandler={pageChangeHandler}
            isLoading={pageStates?.tableLoader}
            totalPages={pageStates?.totalPages}
            count={pageStates?.count}
            handleChangePageSize={handleChangePageSize}
            pageSize={filterDetails.pageSize}
          />
        </CardLayout>
      </div>
      <ViewDocumentModal
        isOpen={modal}
        toggle={toggleModal}
        selectedDocument={selectedDocument}
      />
    </Layout>
  );
};
export default MySlips;
